import { z } from 'zod';
import { MongoObjectIdSchema } from '@terra/shared-utils';

export const ApplicationDuplicationModeSchema = z.enum([
  'SAME_TENANT',
  'DIFFERENT_TENANT',
  'CHANGE_URL',
]);

export const ApplicationDuplicationScopeSchema = z.enum([
  'FULL',
  'BASIC',
  'ENDPOINTS',
  'SIGNALS',
  'FINDINGS',
  'FINDINGS_ONLY',
  'ENDPOINTS_AND_FINDINGS',
]);

export const ApplicationDuplicationFindingSelectionSchema =
  z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('all') }),
    z.object({ mode: z.literal('published') }),
    z.object({ mode: z.literal('unpublished') }),
    z.object({
      mode: z.literal('ids'),
      findingIds: z.array(MongoObjectIdSchema).min(1).max(5000),
    }),
  ]);

export const StartApplicationDuplicationJobRequestSchema = z.object({
  sourceApplicationId: MongoObjectIdSchema,
  mode: ApplicationDuplicationModeSchema,
  scope: ApplicationDuplicationScopeSchema,
  mongoUri: z.string().trim().min(1),
  createTarget: z.boolean(),
  targetTenantId: z.string().min(1).optional(),
  targetApplicationId: MongoObjectIdSchema.optional(),
  targetName: z.string().min(1).max(200).optional(),
  targetUrl: z.url().optional(),
  findingSelection: ApplicationDuplicationFindingSelectionSchema.optional(),
});

export const ApplicationDuplicationJobStatusSchema = z.enum([
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
]);

export const ApplicationDuplicationJobStageSchema = z.enum([
  'QUEUED',
  'CONNECTING',
  'RESOLVING_FINDINGS',
  'PREPARING_TARGET',
  'COPYING_COLLECTIONS',
  'FINALIZING',
  'DONE',
]);

export const ApplicationDuplicationCollectionCountSchema = z.object({
  database: z.string(),
  collection: z.string(),
  copied: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});

export const ApplicationDuplicationJobSchema = z.object({
  id: z.string(),
  status: ApplicationDuplicationJobStatusSchema,
  mode: ApplicationDuplicationModeSchema,
  scope: ApplicationDuplicationScopeSchema,
  sourceApplicationId: z.string(),
  sourceTenantId: z.string().nullable(),
  targetApplicationId: z.string().nullable(),
  targetTenantId: z.string().nullable(),
  targetName: z.string().nullable(),
  targetUrl: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  errorMessage: z.string().nullable(),
  collectionCounts: z.array(ApplicationDuplicationCollectionCountSchema),
  totalCopied: z.number().int().nonnegative(),
  stage: ApplicationDuplicationJobStageSchema.default('QUEUED'),
  collectionsTotal: z.number().int().nonnegative().default(0),
  collectionsCompleted: z.number().int().nonnegative().default(0),
  currentCollection: z.string().nullable().default(null),
});

export const ApplicationDuplicationJobParamsSchema = z.object({
  jobId: z.string().min(1),
});

export const StartApplicationDuplicationJobResponseSchema = z.object({
  job: ApplicationDuplicationJobSchema,
});

export type ApplicationDuplicationMode = z.infer<
  typeof ApplicationDuplicationModeSchema
>;
export type ApplicationDuplicationScope = z.infer<
  typeof ApplicationDuplicationScopeSchema
>;
export type ApplicationDuplicationJobStage = z.infer<
  typeof ApplicationDuplicationJobStageSchema
>;
export type ApplicationDuplicationJob = z.infer<
  typeof ApplicationDuplicationJobSchema
>;
export type StartApplicationDuplicationJobRequest = z.infer<
  typeof StartApplicationDuplicationJobRequestSchema
>;
